import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import clsx from "clsx";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import FormInputLabel from "./FormInputLabel";
import FormTextInput from "./FormTextInput";
import InfoCard from "./InfoCard";

export type FormDateInputProps = {
    id: string;
    label: string;
    value: Date | null;
    onChange: (value: Date | null) => void;
    error?: FieldError;
    minDate?: Date;
    maxDate?: Date;
    required?: boolean;
    hideHelpText?: boolean;
    helperText?: string;
    infoText?: string;
};

const FormDateInput = ({
    id,
    label,
    value,
    onChange,
    error,
    minDate,
    maxDate,
    required,
    hideHelpText,
    helperText = "dd/mm/yyyy",
    infoText,
}: FormDateInputProps) => {
    const [moreInfoIsExpanded, setMoreInfoIsExpanded] = useState(false);
    const moreInfoLabelId = `${label}-more-info`;
    const moreInfoPanelId = `${label}-more-info-panel`;
    return (
        <div className="w-full">
            <div className="flex justify-between">
                <FormInputLabel htmlFor={id} required={required}>
                    {label}
                </FormInputLabel>
                {infoText && (
                    <button
                        className="text-sm underline"
                        id={moreInfoLabelId}
                        aria-expanded={moreInfoIsExpanded}
                        aria-controls={moreInfoPanelId}
                        onClick={() => setMoreInfoIsExpanded((prev) => !prev)}
                        aria-label={
                            moreInfoIsExpanded ? "hide info" : undefined
                        }
                        type="button"
                    >
                        <span className="sr-only">{label} </span>info
                    </button>
                )}
            </div>
            <DesktopDatePicker
                inputFormat="dd/MM/yyyy"
                value={value}
                onOpen={() => {
                    if (!value && minDate) {
                        onChange(minDate);
                    }
                }}
                onChange={onChange}
                minDate={minDate}
                maxDate={maxDate}
                renderInput={(params) => (
                    <div className="relative" ref={params.ref}>
                        <FormTextInput
                            id={id}
                            className="w-full"
                            ref={params.inputRef}
                            error={error}
                            helpText={hideHelpText ? undefined : helperText}
                            {...params.inputProps}
                        />
                        <span
                            className={clsx(
                                "mt-[26px] absolute top-0",
                                error ? "right-12" : "right-4"
                            )}
                        >
                            {params.InputProps?.endAdornment}
                        </span>
                    </div>
                )}
            />
            {infoText && (
                <InfoCard
                    className="p-3 mt-4"
                    id={moreInfoPanelId}
                    aria-labelledby={moreInfoLabelId}
                    hidden={!moreInfoIsExpanded}
                >
                    {label && <h3 className="font-bold mb-1">{label}</h3>}
                    {infoText}
                </InfoCard>
            )}
        </div>
    );
};

export default FormDateInput;
